import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import TextField from "@components/ui/textfields/TextField";
import Button from "@components/ui/buttons/Button";
import Icon1 from "@assets/icons/Icon";
import firebase from "@services/firebase";
import { raiseError } from "context/error.service";
import { showNotification } from "context/notification.service";

export default function ResetPasswordScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get("oobCode");

  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!oobCode) {
        setValidationError(
          "Lien de réinitialisation invalide ou incomplet."
        );
        setIsValidating(false);
        return;
      }
      try {
        const verifiedEmail = await verifyPasswordResetCode(
          firebase.auth,
          oobCode
        );
        setEmail(verifiedEmail);
      } catch (error: any) {
        const code = error?.code || "";
        if (code === "auth/expired-action-code") {
          setValidationError(
            "Ce lien a expiré. Demandez un nouvel email de réinitialisation."
          );
        } else if (code === "auth/invalid-action-code") {
          setValidationError(
            "Ce lien est invalide ou a déjà été utilisé."
          );
        } else if (code === "auth/user-disabled") {
          setValidationError("Ce compte a été désactivé.");
        } else if (code === "auth/user-not-found") {
          setValidationError("Aucun compte ne correspond à ce lien.");
        } else {
          setValidationError(
            "Impossible de valider ce lien. Veuillez réessayer."
          );
        }
      } finally {
        setIsValidating(false);
      }
    };

    validate();
  }, [oobCode]);

  const handleResetPassword = async () => {
    if (!password) {
      raiseError({
        type: "Champ requis",
        reason: "Veuillez entrer un nouveau mot de passe",
        solutions: ["Choisissez un mot de passe pour votre compte"],
      });
      return;
    }

    if (password.length < 6) {
      raiseError({
        type: "Mot de passe trop court",
        reason: "Le mot de passe doit contenir au moins 6 caractères",
        solutions: ["Choisissez un mot de passe plus long et sécurisé"],
      });
      return;
    }

    if (password !== confirmPassword) {
      raiseError({
        type: "Mots de passe différents",
        reason: "Les mots de passe ne correspondent pas",
        solutions: ["Assurez-vous que les deux mots de passe sont identiques"],
      });
      return;
    }

    if (!oobCode) return;

    setIsLoading(true);
    try {
      await confirmPasswordReset(firebase.auth, oobCode, password);
      setDone(true);
      showNotification({
        type: "success",
        message: "Votre mot de passe a été réinitialisé avec succès",
      });
    } catch (error: any) {
      const code = error?.code || "";
      let reason = "Impossible de réinitialiser votre mot de passe";
      if (code === "auth/expired-action-code") {
        reason = "Ce lien a expiré. Demandez un nouvel email de réinitialisation.";
      } else if (code === "auth/invalid-action-code") {
        reason = "Ce lien est invalide ou a déjà été utilisé.";
      } else if (code === "auth/weak-password") {
        reason = "Le mot de passe est trop faible.";
      }
      raiseError({
        type: "Erreur",
        reason,
        solutions: [
          "Vérifiez que le mot de passe respecte les exigences (minimum 6 caractères)",
          "Si le lien a expiré, demandez un nouvel email de réinitialisation",
        ],
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center px-6">
        <div className="text-h2 text-neutral-400">
          Validation du lien de réinitialisation...
        </div>
      </div>
    );
  }

  if (validationError) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center px-6">
        <div className="flex flex-col items-center gap-4 max-w-400 w-full">
          <Icon1 name="erreur" size={60} color="#EF4444" />
          <div className="text-hx2 font-bold text-center">
            Lien invalide
          </div>
          <div className="text-h3 text-neutral-400 text-center">
            {validationError}
          </div>
          <Button
            variant="large"
            label="Retour à la connexion"
            onClick={() => navigate("/login")}
          />
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center px-6">
        <div className="flex flex-col items-center gap-4 max-w-400 w-full">
          <div className="border p-4 rounded-full flex flex-col justify-center items-center mb-4">
            <Icon1 name="done" size={50} color="#10B981" />
          </div>
          <div className="text-hx2 font-bold text-center">
            Mot de passe réinitialisé
          </div>
          <div className="text-h3 text-neutral-400 text-center">
            Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
          </div>
          <Button
            variant="large"
            label="Se connecter"
            onClick={() => navigate("/login")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen flex flex-row">
      <div className="flex w-full flex-col items-center justify-center px-6 py-10 min-h-screen">
        <form
          className="max-w-400 w-full flex flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            handleResetPassword();
          }}
        >
          <div className="items-center flex flex-col gap-1">
            <div className="border p-4 rounded-full flex flex-col justify-center items-center mb-4">
              <Icon1 name="user" size={50} color="lightgrey" />
            </div>

            <div className="text-hx3 font-bold">
              Réinitialiser votre mot de passe
            </div>
            <div className="text-hx1 mb-4 text-neutral-400 text-center">
              Choisissez un nouveau mot de passe pour votre compte
            </div>

            <TextField
              type="email"
              label="Email"
              value={email}
              disabled={true}
              className="mb-2"
            />

            <TextField
              type="password"
              label="Nouveau mot de passe"
              placeholder="******"
              value={password}
              onChange={(value) => setPassword(value)}
              className="mb-2"
              disabled={isLoading}
            />

            <TextField
              type="password"
              label="Confirmer le mot de passe"
              placeholder="******"
              value={confirmPassword}
              onChange={(value) => setConfirmPassword(value)}
              className="mb-8"
              disabled={isLoading}
            />

            <Button
              variant="large"
              disabled={isLoading}
              loading={isLoading}
              type="submit"
              label={
                isLoading
                  ? "Réinitialisation..."
                  : "Réinitialiser mon mot de passe"
              }
            />
          </div>
        </form>

        <div className="mt-8 text-h3 text-neutral-400">© 2026 HexaFlow</div>
      </div>

      <div className="p-2 lg:flex hidden w-full max-w-[50%]">
        <div
          style={{ borderRadius: 60 }}
          className="bg-black h-full w-full relative flex flex-col justify-between"
        >
          <div
            style={{ borderRadius: 60 }}
            className="absolute w-full items-center justify-center flex flex-col h-full"
          >
            <img
              style={{ borderRadius: 60 }}
              className="object-cover opacity-50 w-full h-full object-center"
              src="/welcome2.webp"
              alt=""
            />
          </div>

          <div className="flex flex-col p-10 z-10">
            <div className="font-bold text-white text-hx3">Pilot</div>
            <div className="text-hx1 mt-1 text-neutral-300 max-w-400">
              Réinitialisation sécurisée de votre mot de passe.
            </div>
          </div>

          <div className="flex flex-row items-start w-full max-w-600 justify-start p-10 gap-10 z-10">
            <div className="flex flex-col">
              <div className="text-h1 text-white">Sécurité</div>
              <div className="text-h2 mt-1 text-neutral-300">
                Choisissez un mot de passe robuste, de préférence différent de ceux
                que vous utilisez sur d'autres sites.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
